import { Box, Button } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import React, { FC, useCallback, useRef } from 'react';

import { TLocalesKeys as K } from '../../../../types';
import { ITicketItem, ITicketSelector } from '../../../common/messageCreators';
import { useScrollIntoView, useUserActionClickSelector } from '../../hooks';
import { MessageItem } from '../MessageItem';
import { SelectorFooter, SelectorWrapper } from '../Selectors';
import { usePagination } from '../Selectors/usePagination';
import { TicketListItem } from './TicketListItem';

export const TicketSelector: FC<ITicketSelector> = ({ id, type, items, pageSize, cta }) => {
  const { t } = useTranslation();
  const emit = useUserActionClickSelector();
  const { hasMore, visibleItems, focusIndex, loadMore } = usePagination(items, pageSize);
  const ref = useRef<HTMLDivElement>(null);
  const scrollIntoView = useScrollIntoView(ref);

  const onClick = useCallback(
    (item: ITicketItem, position: number) => {
      emit(type, {
        label: item.label,
        item,
        position,
      });
    },
    [emit, type],
  );

  const onClickCTA = useCallback(() => {
    const response = t<K>('ticket_cta_label');

    emit(type, {
      label: response,
      item: {
        cta: true,
        response,
      },
    });
  }, [emit, t, type]);

  const onLoadMoreClick = useCallback(() => {
    loadMore();
    scrollIntoView();
  }, [loadMore, scrollIntoView]);

  const footer = hasMore && <SelectorFooter label={t<K>('selector_view_more')} onClick={onLoadMoreClick} />;

  return (
    <MessageItem id={id} ref={ref}>
      <Box direction="vertical" gap="SP2">
        <SelectorWrapper>
          {visibleItems.map((item, i) => (
            <TicketListItem
              key={item.id}
              item={item}
              position={i}
              onClick={onClick}
              hasFocus={i === focusIndex}
            />
          ))}
          {footer}
        </SelectorWrapper>
        {cta && (
          <Box>
            <Button priority="secondary" onClick={onClickCTA} dataHook="cta">
              {t<K>('ticket_cta_label')}
            </Button>
          </Box>
        )}
      </Box>
    </MessageItem>
  );
};
